import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

export const fetchNotifications = createAsyncThunk(
  "notification/fetchAll",
  async ({ page = 1, limit = 10, search = "" }, thunkAPI) => {
    try {
      /* keeps its {data, pagination} envelope, unlike most of the new API */
      const res = await api.get("/admin/notifications", { params: { page, limit, search } });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch notifications");
    }
  }
);

export const createNotification = createAsyncThunk(
  "notification/create",
  async (data, thunkAPI) => {
    try {
      /* 201 {data: row} — this one IS enveloped; the PUT/PATCH below are not */
      const res = await api.post("/admin/notifications", data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to create");
    }
  }
);

export const updateNotification = createAsyncThunk(
  "notification/update",
  async ({ id, data }, thunkAPI) => {
    try {
      /* bare {id, title, body, editedBy} — a PARTIAL row, so the reducer merges */
      const res = await api.put(`/admin/notifications/${id}`, data);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Failed to update");
    }
  }
);

export const toggleNotificationStatus = createAsyncThunk(
  "notification/toggleStatus",
  async (id, thunkAPI) => {
    try {
      /* bare {id, isActive} — the flipped value is authoritative, never computed here */
      const res = await api.patch(`/admin/notifications/${id}/status`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update status");
    }
  }
);

export const deleteNotification = createAsyncThunk(
  "notification/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/admin/notifications/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message || "Delete failed");
    }
  }
);

const notificationSlice = createSlice({
  name: "notification",
  initialState: {
    notifications: [],
    loading: false,
    error: null,
    pagination: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchNotifications.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createNotification.fulfilled, (state) => {
        state.pagination.total = (state.pagination.total || 0) + 1;
      })
      .addCase(createNotification.rejected, (state, action) => { state.error = action.payload; })

      /* both writes answer with a partial row keyed by `id` — merge, never
         replace, or the columns they leave out are blanked in the table */
      .addCase(updateNotification.fulfilled, (state, action) => {
        const index = state.notifications.findIndex((n) => n.id === action.payload.id);
        if (index !== -1) state.notifications[index] = { ...state.notifications[index], ...action.payload };
      })
      .addCase(updateNotification.rejected, (state, action) => { state.error = action.payload; })

      .addCase(toggleNotificationStatus.fulfilled, (state, action) => {
        const index = state.notifications.findIndex((n) => n.id === action.payload.id);
        if (index !== -1) state.notifications[index] = { ...state.notifications[index], ...action.payload };
      })

      .addCase(deleteNotification.fulfilled, (state) => {
        state.pagination.total = (state.pagination.total || 1) - 1;
      })
      .addCase(deleteNotification.rejected, (state, action) => { state.error = action.payload; });
  },
});

export default notificationSlice.reducer;